import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { formatDateTime } from "../../api/dates";
import { useCorrectExtraction, useExtraction, useExtractionHistory, useExtractionQueue } from "../../api/delivery";
import { Card, PageHead } from "../../components/ui";
import { useDebounced } from "../../hooks/useDebounced";
import "../../styles/upload.css";

/** What the extractor read is corrected by its application field name. */
const FIELDS: [string, string][] = [
  ["site_name", "Site"],
  ["address", "Address"],
  ["rs_no", "RS no. (works project)"],
  ["valve_desc", "Valve description"],
  ["valve_make", "Valve make"],
  ["valve_type", "Valve type"],
  ["valve_serial", "Serial"],
  ["dn_scope", "DN (scope)"],
  ["dn_boq", "DN (BOQ)"],
  ["func_loc", "Functional location"],
  ["equip_no", "Equipment no."],
  ["notif_doc", "Works order (notification no.)"],
  ["station_type", "Station type"],
  ["contract_no", "Contract no."],
];

function percent(value: unknown): string {
  return typeof value === "number" ? `${Math.round(value * 100)}%` : "";
}

/** The QCP validation queue: what the extractor was least sure of, and each
 * document's fields corrected in place, every change audited. */
export default function ExtractionPage() {
  const { id } = useParams();
  const selected = id === undefined ? null : Number(id);
  const navigate = useNavigate();
  const [batchId, setBatchId] = useState("");
  const queue = useExtractionQueue(useDebounced(batchId.trim()));
  const rows = queue.data?.extractions ?? [];

  return (
    <div className="upload-page single">
      <div className="upload-main">
        <PageHead
          title="Extraction queue"
          sub={
            queue.data
              ? `Documents read below ${Math.round(queue.data.reviewBelow * 100)}% confidence, least sure first.`
              : "Documents the extractor was least sure of."
          }
          actions={
            <Link className="go-link" to="/ingest">
              ← Upload
            </Link>
          }
        />
        <div className="toolbar">
          <label className="check">
            Batch
            <input
              className="input upload-batch-input"
              value={batchId}
              onChange={(e) => setBatchId(e.target.value)}
              inputMode="numeric"
              placeholder="all"
            />
          </label>
          <span className="count-badge">{queue.data?.count ?? 0}</span>
        </div>

        {selected === null ? null : <ExtractionDetail key={selected} id={selected} />}

        <section className="table-card" aria-label="Extraction queue">
          {queue.isError ? (
            <p className="notice bad upload-note" role="alert">
              Could not load the queue: {queue.error.message}
            </p>
          ) : null}
          {queue.isPending ? (
            <p className="empty-state">Loading…</p>
          ) : rows.length === 0 && !queue.isError ? (
            <p className="empty-state">Nothing waiting for review.</p>
          ) : rows.length > 0 ? (
            <table className="data-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Location</th>
                  <th>Brand</th>
                  <th>Type</th>
                  <th>RS no.</th>
                  <th className="num">Confidence</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr
                    key={row.id}
                    className={row.id === selected ? "clickable selected" : "clickable"}
                    tabIndex={0}
                    onClick={() => navigate(`/extraction/${row.id}`)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        navigate(`/extraction/${row.id}`);
                      }
                    }}
                  >
                    <td className="mono">{row.id}</td>
                    <td>{String(row.location ?? "")}</td>
                    <td>{String(row.brand ?? "")}</td>
                    <td>{String(row.valveType ?? "")}</td>
                    <td className="mono">{String(row.worksProjectNo ?? "")}</td>
                    <td className="num">{percent(row.confidence)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : null}
        </section>
      </div>
    </div>
  );
}

function ExtractionDetail({ id }: { id: number }) {
  const row = useExtraction(id);
  const history = useExtractionHistory(id);
  const correct = useCorrectExtraction(id);
  const [edits, setEdits] = useState<Record<string, string>>({});
  const [note, setNote] = useState("");

  if (row.isPending) {
    return <p className="empty-state">Loading document…</p>;
  }
  if (row.isError) {
    return (
      <p className="notice bad" role="alert">
        {row.error.status === 404 ? "There is no document with that number." : `Could not load the document: ${row.error.message}`}
      </p>
    );
  }
  const data = row.data;
  const fields = (data.fields ?? {}) as Record<string, { value?: unknown; confidence?: number; override?: unknown }>;

  return (
    <Card
      title={`Document #${id}`}
      sub={String(data.location ?? "")}
      actions={
        typeof data.batchId === "number" ? (
          <Link className="go-link" to={`/ingest/${data.batchId}`}>
            Batch #{data.batchId}
          </Link>
        ) : undefined
      }
    >
      <form
        className="upload-form"
        onSubmit={(e) => {
          e.preventDefault();
          const body: Record<string, unknown> = { ...edits };
          if (note.trim()) {
            body.note = note.trim();
          }
          correct.mutate(body, { onSuccess: () => setEdits({}) });
        }}
      >
        {FIELDS.map(([name, label]) => {
          const field = fields[name];
          const current = field?.override ?? field?.value ?? data[name] ?? "";
          const edited = edits[name] !== undefined;
          return (
            <label key={name} className={edited ? "field upload-field-edited" : "field"}>
              <span>
                {label}
                {typeof field?.confidence === "number" ? <span className="upload-confidence"> {percent(field.confidence)}</span> : null}
              </span>
              <input value={edits[name] ?? String(current ?? "")} onChange={(e) => setEdits({ ...edits, [name]: e.target.value })} />
            </label>
          );
        })}
        <label className="field upload-form-wide">
          Why
          <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Optional note kept with each correction" />
        </label>
        <div className="upload-form-wide upload-save-row">
          <button type="submit" className="btn btn-primary" disabled={correct.isPending || Object.keys(edits).length === 0}>
            {correct.isPending ? "Saving…" : "Save corrections"}
          </button>
          {correct.data ? (
            <span className="upload-hint" role="status">
              Corrected: {correct.data.corrected.join(", ") || "nothing changed"}
            </span>
          ) : null}
        </div>
        {correct.isError ? <p className="notice bad upload-form-wide">{correct.error.message}</p> : null}
      </form>
      <h3 className="section-label upload-history-label">Corrections so far</h3>
      {history.data && history.data.edits.length > 0 ? (
        <table className="data-table">
          <thead>
            <tr>
              <th>When</th>
              <th>Field</th>
              <th>From</th>
              <th>To</th>
              <th>By</th>
            </tr>
          </thead>
          <tbody>
            {history.data.edits.map((edit, index) => (
              <tr key={`${edit.at}-${index}`}>
                <td className="muted">{formatDateTime(edit.at)}</td>
                <td>{edit.field}</td>
                <td>{String(edit.from ?? "")}</td>
                <td>{String(edit.to ?? "")}</td>
                <td className="muted">{edit.by ?? "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <p className="upload-hint">None.</p>
      )}
    </Card>
  );
}
